import dayjs from "dayjs";

const TIMEZONE_SUFFIX_PATTERN = /(?:Z|[+-]\d{2}:?\d{2}|GMT|UTC)$/i;

export class ViewModelContractError extends Error {
    constructor(message, details = []) {
        super(message);
        this.name = "ViewModelContractError";
        this.details = Array.isArray(details) ? details.filter(Boolean) : [details].filter(Boolean);
    }
}

export function failContract(message, details = []) {
    throw new ViewModelContractError(message, details);
}

export function isViewModelContractError(error) {
    return error instanceof ViewModelContractError || error?.name === "ViewModelContractError";
}

export function formatContractError(error, fallback) {
    if (!isViewModelContractError(error)) return fallback || error?.message;
    if (!error.details?.length) return error.message || fallback;
    return `${error.message}: ${error.details.join("；")}`;
}

export function isRecord(value) {
    return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

export function hasOwn(record, key) {
    return Object.prototype.hasOwnProperty.call(record, key);
}

export function requireRecord(value, path) {
    if (!isRecord(value)) {
        failContract("响应契约错误", `${path} 必须是对象`);
    }
    return value;
}

export function requireArray(value, path) {
    if (!Array.isArray(value)) {
        failContract("响应契约错误", `${path} 必须是数组`);
    }
    return value;
}

export function requireNumber(value, path, { integer = false, min = null } = {}) {
    if (typeof value !== "number" || !Number.isFinite(value)) {
        failContract("响应契约错误", `${path} 必须是数字`);
    }
    if (integer && !Number.isInteger(value)) {
        failContract("响应契约错误", `${path} 必须是整数`);
    }
    if (min !== null && value < min) {
        failContract("响应契约错误", `${path} 不能小于 ${min}`);
    }
    return value;
}

export function requireBoolean(value, path) {
    if (typeof value !== "boolean") {
        failContract("响应契约错误", `${path} 必须是布尔值`);
    }
    return value;
}

export function requireString(value, path, { allowEmpty = false } = {}) {
    if (typeof value !== "string") {
        failContract("响应契约错误", `${path} 必须是字符串`);
    }
    const normalized = value.replace(/\s+/g, " ").trim();
    if (!allowEmpty && !normalized) {
        failContract("响应契约错误", `${path} 不能为空`);
    }
    return normalized;
}

export function optionalString(value, path) {
    if (value === null || value === undefined) return "";
    return requireString(value, path, { allowEmpty: true });
}

export function requireKnownEnum(value, languageMap, path) {
    const key = requireString(value, path).toLowerCase();
    if (!hasOwn(languageMap, key)) {
        failContract("响应契约错误", `${path} 返回未知枚举 ${key}`);
    }
    return { key, value: languageMap[key] };
}

export function assertKnownEnumKey(value, allowedValues, path) {
    const key = requireString(value, path).toLowerCase();
    if (!allowedValues.has(key)) {
        failContract("响应契约错误", `${path} 返回未知枚举 ${key}`);
    }
    return key;
}

export function requireTimezoneAwareDateTime(value, path) {
    const text = requireString(value, path);
    if (!TIMEZONE_SUFFIX_PATTERN.test(text)) {
        failContract("响应契约错误", `${path} 缺少明确时区`);
    }

    const parsed = dayjs(text);
    if (!parsed.isValid()) {
        failContract("响应契约错误", `${path} 不是可解析时间`);
    }
    return parsed;
}
